import { auth, db } from '../firebaseConfig';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, updateProfile, sendPasswordResetEmail, signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { collection, doc, getDoc, query, where, setDoc, getDocs } from 'firebase/firestore';
import { defaultPreferences } from '../constants/defaultPreferences';



export const authService = {
  // Login with email or username and password
  login: async (email, password) => {

    try {
      // Proceed with Firebase Auth login using the email
      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      // Get additional user data from Firestore
      let userData = null;
      
      // First check if user exists in Firestore
      const userRef = doc(db, 'users', userCredential.user.uid);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists) {
        // User exists in Firestore, get their data
        userData = userDoc.data();
      } else  {
        throw new Error('User not found');
      }
      
      return { 
        user: {
          ...userCredential.user,
          ...userData
        }, 
        error: null 
      };
    } catch (error) {
      console.log(error);
      let errorMessage = 'An error occurred during login';
      
      // Handle specific Firebase auth errors
      console.log(error.code);
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = 'No account found with this email/username';
          break;
        case 'auth/wrong-password':
          errorMessage = 'Incorrect password';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Too many failed attempts. Please try again later';
          break;
        case 'auth/invalid-credential':
          errorMessage = 'Invalid email/username or password';
          break;
        case 'auth/user-disabled':
          errorMessage = 'This account has been disabled';
          break;
      }
      
      return { user: null, error: errorMessage };
    }
  },

  // Register new user
  register: async (email, password) => {
    try {
      console.log('Starting registration process for:', email);
      
      // Check if email already exists
      const emailQueryRef = query(
        collection(db, 'users'),
        where('email', '==', email)
      );
      const emailQuery = await getDocs(emailQueryRef);
      
      if (!emailQuery.empty) {
        console.log('Email already exists');
        return { user: null, error: 'Email already in use' };
      }
      
      console.log('Creating user with Firebase Auth...');
      // Create user with email and password
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      console.log('User created successfully:', userCredential.user.uid);
      
      
      // Then create the user profile in Firestore with default preferences
      const userData = {
        uid: userCredential.user.uid,
        username: email.split('@')[0], // Default username from email
        email,
        phoneNumber: '',
        profilePhoto: '',
        createdAt: new Date(),
        preferences: defaultPreferences
      };

      
      const userRef = doc(db, 'users', userCredential.user.uid);
      await setDoc(userRef, userData);

      const updatedUser = await getDoc(userRef);
      
      return { 
        user: {
          ...userCredential.user,
          ...updatedUser.data()
        }, 
        error: null 
      };
    } catch (error) {
      console.error('Registration error:', error);
      let errorMessage = 'An error occurred during registration';
      
      // Handle specific Firebase auth errors
      switch (error.code) {
        case 'auth/email-already-in-use':
          errorMessage = 'An account already exists with this email';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Invalid email address';
          break;
        case 'auth/weak-password':
          errorMessage = 'Password is too weak';
          break;
        case 'auth/network-request-failed':
          errorMessage = 'Network error. Please check your connection';
          break;
        default:
          // Check for blocked requests
          if (error.message && error.message.includes('ERR_BLOCKED_BY_CLIENT')) {
            errorMessage = 'Request blocked by browser extension. Please disable ad blockers or try incognito mode.';
          } else if (error.message && error.message.includes('Failed to fetch')) {
            errorMessage = 'Network request failed. Please check your internet connection and try again.';
          } else {
            errorMessage = error.message || 'Registration failed';
          }
      }
      
      return { user: null, error: errorMessage };
    }
  },

  // Logout
  logout: async () => {
    try {
      await auth.signOut();
      return { error: null };
    } catch (error) {
      return { error: error.message };
    }
  },

  // Reset password
  resetPassword: async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      return { error: null };
    } catch (error) {
      console.log(error);
      return { error: error.message };
    }
  },

  // Google Sign-In
  signInWithGoogle: async () => {
    try {
      console.log('Starting Google Sign-In process...');
      
      const provider = new GoogleAuthProvider();
      provider.addScope('email');
      provider.addScope('profile');
      
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      
      console.log('Google Sign-In successful:', user.uid);
      
      // Check if user exists in Firestore
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      
      let userData;
      if (userDoc.exists()) {
        // User exists, update last login and any new Google data
        userData = userDoc.data();
        await setDoc(userRef, {
          ...userData,
          lastLogin: new Date(),
          displayName: user.displayName || userData.displayName,
          photoURL: user.photoURL || userData.photoURL,
          provider: 'google'
        }, { merge: true });
      } else {
        // New user, create profile with Google data
        userData = {
          uid: user.uid,
          username: user.displayName || user.email.split('@')[0],
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL,
          phoneNumber: user.phoneNumber || '',
          createdAt: new Date(),
          lastLogin: new Date(),
          provider: 'google',
          preferences: defaultPreferences
        };
        
        await setDoc(userRef, userData);
        console.log('New Google user profile created');
      }
      
      return { 
        user: {
          ...user,
          ...userData
        }, 
        error: null 
      };
    } catch (error) {
      console.error('Google Sign-In error:', error);
      let errorMessage = 'An error occurred during Google Sign-In';
      
      // Handle specific Google Sign-In errors
      switch (error.code) {
        case 'auth/popup-closed-by-user':
          errorMessage = 'Sign-in was cancelled';
          break;
        case 'auth/popup-blocked':
          errorMessage = 'Popup was blocked by browser. Please allow popups and try again';
          break;
        case 'auth/network-request-failed':
          errorMessage = 'Network error. Please check your connection';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Too many requests. Please try again later';
          break;
        case 'auth/account-exists-with-different-credential':
          errorMessage = 'An account already exists with this email using a different sign-in method';
          break;
        default:
          // Check for blocked requests
          if (error.message && error.message.includes('ERR_BLOCKED_BY_CLIENT')) {
            errorMessage = 'Request blocked by browser extension. Please disable ad blockers or try incognito mode.';
          } else if (error.message && error.message.includes('Failed to fetch')) {
            errorMessage = 'Network request failed. Please check your internet connection and try again.';
          } else {
            errorMessage = error.message || 'Google Sign-In failed';
          }
      }
      
      return { user: null, error: errorMessage };
    }
  },
}; 